import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReceiptCapture from "@/components/ReceiptCapture";
import ExpenseSummary from "@/components/ExpenseSummary";
import ReceiptList from "@/components/ReceiptList";
import MileageTracker from "@/components/MileageTracker";
import TaxPal from "@/components/TaxPal";
import VaultInbox from "@/components/VaultInbox";
import BankImport from "@/components/BankImport";
import QuarterlyPayments from "@/components/QuarterlyPayments";
import ReferralCard from "@/components/ReferralCard";
import { Vault, FileText, Upload, Loader2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const TaxDocUploader = ({ files, docTypes, onUploaded }) => {
  const ref = useRef(null);
  const [docType, setDocType] = useState("other");
  const [busy, setBusy] = useState(false);
  const upload = async (list) => {
    if (!list?.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of Array.from(list)) {
      const fd = new FormData(); fd.append("file", f); fd.append("doc_type", docType);
      try { await api.post("/intake/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false); if (ref.current) ref.current.value = "";
    if (ok) { toast.success(`${ok} document${ok === 1 ? "" : "s"} saved to your vault`); onUploaded(); }
  };
  const docs = files.filter((f) => f.doc_type !== "receipt");
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="vault-docs">
      <div className="flex items-center gap-2 text-[#114A31]"><FileText className="h-5 w-5" /><span className="font-heading font-semibold">Tax documents</span></div>
      <p className="mt-1 text-sm text-[#595D5A]">W-2s, 1099s, prior returns, bank statements — anything your tax pro will need.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="rounded-xl sm:w-60" data-testid="vault-doctype"><SelectValue /></SelectTrigger>
          <SelectContent>{docTypes.filter((d) => d.key !== "receipt" && d.key !== "signed_form").map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
        </Select>
        <input ref={ref} type="file" multiple accept="image/*,.pdf,.doc,.docx" className="hidden" data-testid="vault-doc-input" onChange={(e) => upload(e.target.files)} />
        <Button onClick={() => ref.current?.click()} disabled={busy} variant="outline" data-testid="vault-doc-upload-btn"
          className="rounded-xl border-dashed border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB] flex-1">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Snap or upload documents
        </Button>
      </div>
      {docs.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="vault-doc-list">
          {docs.map((f) => (
            <li key={f.file_id} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2">
              <FileText className="h-4 w-4 text-[#114A31] shrink-0" />
              <span className="flex-1 truncate text-[#1C1C1C]">{f.original_filename}</span>
              <span className="text-xs text-[#595D5A] shrink-0">{f.doc_label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function VaultPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [params] = useSearchParams();
  const snapMode = params.get("snap") === "1";

  const load = useCallback(async () => {
    try {
      const [r, s, m] = await Promise.all([
        api.get("/vault/receipts", { params: year ? { year } : {} }),
        api.get("/vault/summary", { params: year ? { year } : {} }),
        api.get("/intake/me"),
      ]);
      setReceipts(r.data); setSummary(s.data); setFiles(m.data?.files || []); setRefreshKey((k) => k + 1);
    } catch { toast.error("Could not load your vault"); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => {
    api.get("/vault/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/doc-types").then((r) => setDocTypes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const hasPlan = (user?.purchased_tiers || []).length > 0;

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-12 pt-24 sm:pt-28 pb-24">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] flex items-center gap-2">
          <Vault className="h-3.5 w-3.5" /> Document Vault · Free
        </span>
        <h1 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]" data-testid="vault-title">
          Snap it. We'll read it.
        </h1>
        <p className="mt-2 text-[#595D5A] max-w-2xl">
          Your tax pal in your pocket: photograph receipts, log miles and Tax Pal keeps a live estimate of what you'll owe — and how to owe less.
        </p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="vault-capture-card">
              <div className="flex items-center gap-2 text-[#114A31] mb-4"><Sparkles className="h-5 w-5" /><span className="font-heading font-semibold">Receipts</span></div>
              <ReceiptCapture categories={categories} onSaved={load} highlight={snapMode} />
            </div>

            <div>
              <h2 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-3">
                Saved receipts{year ? ` · ${year}` : ""}
              </h2>
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#114A31]" /> : (
                <ReceiptList receipts={receipts} categories={categories} onChanged={load} />
              )}
            </div>

            <MileageTracker year={year} onChanged={load} />

            <QuarterlyPayments year={year || summary?.years?.[0] || new Date().getFullYear()} refreshKey={refreshKey} onChanged={load} />

            <BankImport categories={categories} hasPlan={hasPlan} onCommitted={load} />

            <VaultInbox />

            <TaxDocUploader files={files} docTypes={docTypes} onUploaded={load} />
          </div>

          <div className="space-y-6">
            <TaxPal year={year || summary?.years?.[0] || new Date().getFullYear()} refreshKey={refreshKey} hasPlan={hasPlan} />
            <ExpenseSummary summary={summary} year={year} years={summary?.years || []} onYearChange={setYear} />
            {!hasPlan && <ReferralCard />}
            {!hasPlan && (
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="vault-upsell">
                <ShieldCheck className="h-5 w-5 text-[#114A31]" />
                <p className="mt-3 font-heading font-semibold text-[#1C1C1C]">Want these turned into your tax return?</p>
                <p className="mt-1 text-sm text-[#595D5A]">Cain Family Tax Service can review your vault, set up your S-Corp and handle filing. Your documents are already here — no re-uploading.</p>
                <Button onClick={() => navigate("/#pricing")} data-testid="vault-see-plans-btn"
                  className="mt-4 w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">See plans <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            )}
            <p className="text-xs text-[#595D5A] px-1">Encrypted storage · only you and your assigned tax team can see these files. Tax Pal estimates and categories are planning guidance, not tax advice.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
